import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { DestroyBase } from "../../../../components/common/ng-destroy-base";
import { NavigationService } from "../../../../navigation/navigation.service";
import { PixIntegrationService } from "../../services/pix-integration-service.service";
import { ValuationEventPropertiesService } from "../../services/valuation-event-properties.service";
import { environment } from "../../../../../environments/environment";
import { EventBusService } from "../../../../services/event-bus.service";
import { AppStorageService } from "../../../../services/app-storage.service";

/**
 * Handles connecting properties to valuation event
 */
@Component({
  selector: 'valuation-event-properties',
  templateUrl: './valuation-event-properties.component.html',
  styleUrls: ['./valuation-event-properties.component.css']
})
export class ValuationEventPropertiesComponent extends DestroyBase implements OnInit {
  /**
   * Gets the VE data
   */
  get data(): any { return this._data; }

  /**
   * Sets the VE data
   */
  @Input() set data(value: any) {
    this._data = value;
    if (value) {
      this.valuationPortfolioId = value.Oid;
      this.projectId = value.ProjectID;
      setTimeout(this.loadProperties.bind(this));
    } else {
      this.displaySelected();
    }
  }

  /**
   * Can be closed? (as Infobar in table view)
   */
  @Input() canClose: boolean;

  /**
  * Displays comps search options
  */
  @Input() compsOptions: boolean;

  /**
* Is detail form?
*/
  @Input() detailForm: boolean;

  /**
  * Portfolio oid
  */
  @Input() portfolioOid: number;

  /**
   * Close pane event
   */
  @Output() closePane: EventEmitter<any> = new EventEmitter();

  /**
 * ValuationPortfolio data
 */
  private _data: number;

  /**
 * ValuationPortfolio Id
 */
  valuationPortfolioId: number;

  /**
* Project Id
*/
  projectId: number;

  /**
* Properties
*/
  properties: any[];

  /**
* Properties groups
*/
  groups: any[];

  /**
 * ValuationPortfolio Id
 */
  groupingType: number;

  /**
 * Enable/disable loader icon
 */
  loaderEnabled = false;

  /**
 * Grouping icon
 */
  get groupingIcon() {
    return this.groupingType === 1 ? './assets/icons/properties_by_region.svg' : './assets/icons/arrow-down-short-wide-solid.svg';
  }

  /**
   * Should the pane be closed?
   */
  shouldClose = false;

  /**
 * Number of steps (no steps, so 0).. here just for close button
 */
  stepsCount = 0;

  /**
   * Current step
   */
  currentStep = 1;

  /**
   * Selected property
   */
  selectedProperty: any;

  /**
  * Displays/hides comps search button
  */
  showSearchCompButton = false;

  /**
  * Displays/hides delte comps search button
  */
  showDeleteSearchCompButton = false;

  /**
  * Displays/hides preview comps button
  */
  showPreviewCompsButton = false;

  /**
  * Displays/hides comps sidebar
  */
  showPreviewSidebar = false;

  /**
  * Displays/hides comps sidebar
  */
  showPreviewDeleteSidebar = false;

  /**
  * Disables/enables comps search button
  */
  disableSearchCompsButton = false;

  /**
   * Close was clicked
   */
  onCloseClicked() {
    this.shouldClose = true;
    setTimeout(() => {
      this.closePane.emit();
    }, 500);
  }
  /**
   * Is Comps Search Requested
   */
  compsSearchRequested = false;

  /**
   * Flag indicating whether all checkboxes are checked
   */
  public isAllCheckBoxChecked = false;

  /**
   * Flag indicating whether the "Select All" option is active
   */
  public isSelectAllActive = false;

  /**
   * Flag indicating whether delete search comps can be deleted
   */
  public disableDeleteSearchCompButton = false;

  /**
   * Constructor
   * @param eventBus
   * @param appStorage
   * @param navigationService Navigation service for managing navigation within the application
   * @param service Valuation Event Properties service for managing valuation event properties
   * @param _pixIntegrationService PIX Integration service for integrating with PIX
   * @param ref
   */
  constructor(private eventBus: EventBusService,
    private appStorage: AppStorageService,
    private navigationService: NavigationService,
    private service: ValuationEventPropertiesService,
    private _pixIntegrationService: PixIntegrationService,
    private ref: ChangeDetectorRef) {
    super();
  }

  /**
   * Group properties
   */
  ngOnInit(): void {
    this.subscriptions.add(
      this.navigationService.activeValuationEventPropertiesGrouping$.subscribe((groupingType) => {
        this.groupingType = groupingType;
        this.groupProperties();
      }));

    this.onEvents.push(
      this.eventBus.subscribeEvent('appStorage:setSelectedPortfolio', (item) => {
        setTimeout(() => {
          this.data = item;
        });
      })
    );

    if (!this.data) {
      this.displaySelected();
    }
  }

  /**
   * Sets the selected portfolio from the app storage as the data to display
   */
  displaySelected() {
    const item = this.appStorage.getSelectedPortfolio();
    if (item) {
      this.data = item;
    }
  }

  /**
   * Sets the grouping type and calls the method to group the properties accordingly
   * @param groupingType The grouping type to set
   */
  setGroupingType(groupingType: number) {
    this.groupingType = groupingType;
    this.groupProperties();
  }

  /**
   * Loads the properties for the valuation event
   */
  loadProperties(): void {
    // Clear the properties and groups arrays
    this.properties = [];
    this.groups = [];

    this.showProgress(true);
    this.service.loadPropertiesForValuationEvent(this.valuationPortfolioId, this.projectId).subscribe((data) => {
      this.showProgress(false);

      const properties = (data || []).map(p => {
        return {
          oid: p.Oid,
          propertyName: p.buildingName,
          country: p.CountryIDDescription,
          city: p.MunicipalManual,
          subjectPropertyId: p.SubjectPropertyId,
          hasSalesComparables: p.hasSalesComparables,
          hasLeaseComparables: p.hasLeaseComparables,
          hasIEComparables: p.hasIEComparables,
          address: `${(p.Street1 || '')} ${(p.StreetNo1 || '')}, ${(p.houseNoTo || '')}`,
          propertyType: p.PropertyType,
          linkedToPortfolio: !!p.LinkedPropertyValuationId,
          valuationEventEffectiveStartDate: p.ValuationEventEffectiveStartDate,
          valuationEventEffectiveEndDate: p.ValuationEventEffectiveEndDate
        };
      }, () => {
        this.showProgress(false);
      });

      this.properties = properties;
      this.groupProperties();
      this.checkChange();
    });
  }

  /**
   * Groups the properties based on the selected grouping type
   */
  private groupProperties() {
    if (this.groupingType === 3) return; // No need for grouping
    if (!this.properties) return; // No need for grouping

    const groupingKey = this.groupingType === 1 ? 'country' : 'propertyType';

    const groupsObject = this.groupBy(this.properties, groupingKey);
    const groups = [];
    const unknownValue = 'Unknown';
    for (const key of Object.keys(groupsObject)) {
      groups.push({
        groupName: key !== '_empty_' ? key : unknownValue,
        items: groupsObject[key]
      });
    }

    this.groups = groups;
  }

  /**
   * Groups an array of objects by a specified property
   * @param arr The array to group
   * @param prop The property to group by
   * @returns An object with the grouped arrays
   */
  private groupBy(arr, prop) {
    return arr.reduce((groups, item) => {
      const val = item[prop] || '_empty_';
      groups[val] = groups[val] || [];
      groups[val].push(item);
      return groups;
    }, {});
  }

  /**
   * Synchronizes the properties area by broadcasting an event
   */
  syncPropertiesArea() {
    setTimeout(() => {
      this.eventBus.broadcastEvent('PropertyLoadData');
    });
  }

  /**
   * Shows or hides the progress loader
   * @param show Indicates whether to show or hide the progress loader
   */
  showProgress(show: boolean): void {
    // Assignment is pretty false, so we will not show the loader (for now)
    if (this.loaderEnabled) {
      this.eventBus.broadcastEvent('project.valuation.showLoader', show);
    }
  }

  /**
   * Handles the assignment of a property to a portfolio asynchronously
   * @param property The property to assign
   * @returns A Promise that resolves with a boolean indicating the success of the assignment
   */
  async handleAssignment(property: any): Promise<boolean> {
    const oid = property.oid;
    const assign = property.linkedToPortfolio;

    return new Promise((resolve) => {
      this.service.assignToPortfolio(assign, oid, this.valuationPortfolioId, this.projectId).subscribe({
        next: (tuple) => {
          const result: boolean = tuple.m_Item1;
          resolve(result);
        }, error: (_error) => {
          resolve(false);
        }
      });
    });
  }

  /**
   * Handles an assignment error by reloading the properties
   */
  handleAssignmentError() {
    // If not success, reload the properties (for now)
    this.loadProperties();
  }

  /**
   * Checks for changes in the property selection and updates the select all checkbox state
   */
  checkChange() {
    const linkedProperties = this.properties.filter(property => property.linkedToPortfolio);
    this.isSelectAllActive = this.properties.length == linkedProperties.length;
  }

  /**
   * Handles the change of a property selection
   * @param property The property that has changed
   */
  async propertyChange(property: any) {
    this.showProgress(true);
    property.linkedToPortfolio = !property.linkedToPortfolio;
    if (!property.linkedToPortfolio) {
      this.isSelectAllActive = false;
    }
    const success = await this.handleAssignment(property);
    this.showProgress(false);
    this.syncPropertiesArea();
    if (!success) {
      this.handleAssignmentError();
    }
    this.checkChange();

    this.updateCompsIconsState();
  }

  /**
   * On selected property change action
   * @param property Selected property
   */
  onSelectedPropertyChange(property: any) {
    this.selectedProperty = property;
    this.updateCompsIconsState();
  }

  /**
   * Checks all checkboxes
   */
  checkAllCheckBox() {
    const promises = [];
    this.properties.forEach(property => {
      property.linkedToPortfolio = this.isSelectAllActive;
      promises.push(this.handleAssignment(property));
    })

    //not so fast if there is a lot of properties, so show progress in case more than 10
    if (promises.length > 10) {
      this.loaderEnabled = true;
      this.showProgress(true);
    }

    Promise.all(promises).then(results => {
      const anyFailed = results.filter(result => !result).length > 0;
      if (anyFailed) {
        this.handleAssignmentError();
      }
    }).catch(() => {
      this.handleAssignmentError();
    }).finally(() => {
      this.showProgress(false);
      this.loaderEnabled = false;
      this.syncPropertiesArea();
    });
  }

  /**
   * Displays comparables preview sidebar
   */
  showPreview() {
    this.showPreviewSidebar = true;
  }

  /**
   * Show delete preview
   */
  showDeletePreview() {
    this.showPreviewDeleteSidebar = true;
  }

  /**
   * On search comparables action
   */
  searchComparables() {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        if (this.selectedProperty) {
          this._pixIntegrationService.createSubjectPropertyId(this.selectedProperty.oid, this.portfolioOid).subscribe(subjectPropertyId => {
            if (subjectPropertyId) {
              window.open(environment.PIX_SEARCH_ROOT_API_URL + 'subjectProperty/' + subjectPropertyId);
              this.updatePropertyAfterCompsSearch(subjectPropertyId);
              this.disableSearchCompsButton = !this.selectedProperty?.hasSalesComparables && !this.selectedProperty?.hasLeaseComparables && !this.selectedProperty?.hasIEComparables;
              this.showDeleteSearchCompButton = true;
              this.compsSearchRequested = !this.selectedProperty?.hasSalesComparables && !this.selectedProperty?.hasLeaseComparables && !this.selectedProperty?.has;
              this.ref.markForCheck();
            }
          });
        }
      },
      cancel: () => {
        //Nothing
      }
    }, 'searchComparablesConfirmDialog');
  }

  /**
   * Sets hasComparables and subjectPropertyId of selected property
   * @param subjectPropertyId Comps subjectPropertyId
   */
  updatePropertyAfterCompsSearch(subjectPropertyId: string) {
    this.selectedProperty.subjectPropertyId = subjectPropertyId;
    this.compsSearchRequested = !this.selectedProperty?.hasSalesComparables && !this.selectedProperty?.hasLeaseComparables && !this.selectedProperty?.hasIEComparables;
  }

  /**
   * Shows/hides comps icons for selected property
   */
  updateCompsIconsState() {
    this.showSearchCompButton = this.selectedProperty?.linkedToPortfolio;
    this.showDeleteSearchCompButton = true;
    this.showPreviewCompsButton = this.selectedProperty?.linkedToPortfolio;
    this.disableSearchCompsButton = this.selectedProperty?.linkedToPortfolio && this.selectedProperty?.subjectPropertyId && (this.selectedProperty?.hasSalesComparables && this.selectedProperty?.hasLeaseComparables && this.selectedProperty?.hasIEComparables);
    this.compsSearchRequested = this.disableSearchCompsButton;
    this.disableDeleteSearchCompButton = !!this.selectedProperty?.linkedToPortfolio && !!this.selectedProperty?.subjectPropertyId && (this.selectedProperty?.hasSalesComparables || this.selectedProperty?.hasLeaseComparables || this.selectedProperty?.hasIEComparables);
  }

  /**
   * Updates property info after comparables
   */
  updatePropertyAfterDeletingComps() {
    this.selectedProperty.subjectPropertyId = null;
    this.compsSearchRequested = !this.selectedProperty?.hasSalesComparables && !this.selectedProperty?.hasLeaseComparables && !this.selectedProperty?.hasIEComparables;
  }

  /**
   * Disable deleting
   */
  disableDelete() {
    this.showDeleteSearchCompButton = false;
  }
  /**
   * Hides delete connected comparables button
   */
  hideCompsDeleteIcon() {
    this.showDeleteSearchCompButton = false;
    this.disableSearchCompsButton = false;
  }

  /**
   * On Group clicked event
   * @param group Grouping
   */
  async groupClicked(group) {
    //assign all not assigned yet
    const properties = group.items.filter(item => !item.linkedToPortfolio);
    if (properties.length) {
      const promises: Promise<boolean>[] = [];

      for (const propertiy of properties) {
        propertiy.linkedToPortfolio = true;
        promises.push(this.handleAssignment(propertiy));
      }

      Promise.all(promises).then((results) => {
        this.syncPropertiesArea();
        let success = true;
        for (const result of results) {
          success = success && result;
          if (!success) {
            this.handleAssignmentError();
          }
        }
      });
    }
  }

  /**
   * Closes comparables sidebar
   */
  closeComparablesSidebar() {
    this.showPreviewSidebar = false;
  }

  /**
 * Closes comparables delete sidebar
 */
  closeComparablesDeleteSidebar() {
    this.showPreviewDeleteSidebar = false;
  }
}
